<template>
	<div>
		<v-card class="mx-auto elevation-0" style="max-width: 500px">
			<v-form ref="signupform" v-model="valid" class="pa-4 pt-6">
				<v-row>
					<v-col class="pb-0">
						<v-select
							v-model="industry"
							name="industry"
							:items="industryList"
							:rules="[v => !!v || getLabel('industryRequired', 'Industry is required')]"
							required
							:label="getLabel('industry', 'Industry')"
							outlined
						>
							<template v-if="tenantId === 'b16e03a6-17bb-4f0c-b316-9ec017e51d5a'" #append-item>
								<v-list-item>
									<v-list-item-content><a href="https://auth.locumbasevets.com/signup">Veterinary</a></v-list-item-content>
								</v-list-item>
							</template>
						</v-select>
					</v-col>
				</v-row>
				<v-row>
					<v-col class="sx-6 pb-0 pt-0">
						<v-text-field v-model="firstname" label="First Name" :rules="[v => !!v || 'First name is required']" outlined required></v-text-field>
					</v-col>
					<v-col class="sx-6 pb-0 pt-0">
						<v-text-field v-model="lastname" label="Last Name" :rules="[v => !!v || 'Last name is required']" outlined required></v-text-field>
					</v-col>
				</v-row>

				<VuePhoneNumberInput
					v-model="phone_number"
					:rules="[v => !!v || 'Mobile number is required']"
					default-country-code="ZA"
					:preferred-countries="['ZA']"
					label="Mobile Number"
					outlined
					required
					size="lg"
					class="mt-0 mb-5"
					:countries-height="40"
					error-color="red"
					color="orange"
					@update="onChangeTelNumber"
				/>

				<v-row>
					<v-col class="pb-0">
						<v-select
							v-model="heardFromWhere"
							name="heardFromWhere"
							:items="heardFromWhereOptions"
							item-text="label"
							:rules="[v => !!v || 'Please choose an option']"
							required
							label="How did you hear about us?"
							outlined
						></v-select>
					</v-col>
				</v-row>

				<v-row v-for="field in getHeardFromFields" :key="field.form">
					<v-col class="py-0">
						<v-text-field v-model="heardFromWhereText" :label="field.label" outlined required></v-text-field>
					</v-col>
				</v-row>

				<v-text-field v-model="email" :rules="emailRules" label="E-mail" outlined required></v-text-field>

				<v-text-field v-model="confirmedEmail" :rules="[emailConfirmationRule]" label="Confirm E-mail" outlined required></v-text-field>

				<v-text-field
					id="password"
					v-model="password"
					label="Password"
					outlined
					:type="showpassword ? 'text' : 'password'"
					:append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
					required
					name="password"
					:rules="passwordRules"
					@click:append="showpassword = !showpassword"
				></v-text-field>

				<v-text-field
					id="confirmpassword"
					v-model="confirmpassword"
					label="Confirm Password"
					:rules="[...passwordRules, passwordConfirmationRule]"
					outlined
					:type="showpassword ? 'text' : 'password'"
					:append-icon="showpassword ? 'mdi-eye' : 'mdi-eye-off'"
					name="confirmpassword"
					required
					@click:append="showpassword = !showpassword"
				></v-text-field>

				<v-checkbox
					v-model="agreement"
					color="green"
					:rules="[v => !!v || 'You must accept the terms and conditions to continue.']"
					label="I accept the terms and conditions"
				>
					<template #label>
						<v-row>
							<v-col class>
								I accept the
								<a target="_blank" :href="terms" @click.stop> terms and conditions </a>
							</v-col>
						</v-row>
					</template>
				</v-checkbox>
			</v-form>

			<!-- General error messages -->
			<p v-for="message in generalError" v-show="generalError.length > 0" :key="message" error class="text-center error--text mt-2">{{ message }}</p>

			<v-card-actions class="pa-5 pt-0">
				<v-spacer />

				<v-btn class="ma-2 pa-5 text-none" :disabled="!valid || loading" :loading="loading" depressed large rounded color="primary" @click="signUp"
					>Continue as a {{ buttontext }}
					<v-icon right>mdi-arrow-right</v-icon>
				</v-btn>

				<v-spacer />
			</v-card-actions>
		</v-card>

		<!-- General Error Snackbar -->
		<v-snackbar v-model="errorSnackbar" top vertical color="white" :timeout="6000">
			<p v-for="message in generalError" v-show="generalError.length > 0" :key="message" error class="text-center black--text mx-2">{{ message }}</p>
			<v-btn icon @click="errorSnackbar = false">
				<v-icon>mdi-close</v-icon>
			</v-btn>
		</v-snackbar>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Auth } from 'aws-amplify';

import store from '@/store';
import axios from 'axios';
import { fetchTenant } from '@/utils/auth.js';

import { User } from '@/models/user.model';

import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';

import { Tracking } from '@/utils/tracking';
import { labelMixin } from '@/mixin/label.mixin';

export default {
	name: 'UserSignupForm',
	components: { VuePhoneNumberInput },
	mixins: [labelMixin],
	props: {
		userType: {
			type: Number,
			default: null,
		},
	},
	data: () => ({
		errorSnackbar: false,
		valid: false,
		toggle_exclusive: 1,
		usertypeSet: false,
		requestSent: false,
		showpassword: false,
		showconfirmpassword: false,

		loading: false,
		userisconfirmed: true,

		firstname: '',
		lastname: '',
		phone_number: null,
		phone_number_formatted: null,

		heardFromWhere: null,
		heardFromWhereText: '',

		//phone number
		phone: null,
		country: null,
		bindProps: {
			mode: 'international',
			placeholder: 'Enter a phone number',
			required: false,
			enabledCountryCode: true,
			enabledFlags: true,
			autocomplete: 'off',
			name: 'telephone',
			maxLen: 25,
			inputOptions: {
				showDialCode: true,
			},
		},

		confirmcode: null,
		email: null,
		confirmedEmail: null,
		industry: null,
		generalError: [],
		checkbox: false,
		agreement: false,
		dialog: false,
		usertypeform: false,
		isLoading: false,
		password: null,
		confirmpassword: null,

		rules: {
			email: v => (v || '').match(/@/) || 'Please enter a valid email',
			length: len => v => (v || '').length >= len || `Invalid character length, required ${len}`,
			password: v =>
				(v || '').match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*(_|[^\w])).+$/) || 'Password must contain an upper case letter, a numeric character, and a special character',

			//
			confirmpassword: v =>
				(v || '').match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*(_|[^\w])).+$/) ||
				'Confirm Password must contain an upper case letter, a numeric character, and a special character',
			//
			required: v => !!v || 'This field is required',
		},

		//vuetify validation rules
		emailRules: [v => !!v || 'Email is required', v => /.+@.+/.test(v) || 'E-mail must be valid'],
		passwordRules: [
			v => !!v || 'Password is required',
			v => (v && v.length >= 5) || 'Password must have 5+ characters',
			v => /(?=.*[A-Z])/.test(v) || 'Must have one uppercase character',
			v => /(?=.*\d)/.test(v) || 'Must have one number',
		],
	}),
	computed: {
		...mapGetters('tenant', ['tenant']),

		...mapGetters('option', ['industryList', 'heardFromOptions']),

		passwordConfirmationRule() {
			return this.password === this.confirmpassword || 'Password must match';
		},

		emailConfirmationRule() {
			return this.email === this.confirmedEmail || 'Email must match';
		},

		buttontext() {
			if (this.userType === User.TYPES.LOCUM.value) {
				return this.getLabel('signUpUserTypeButtonProfessional', User.TYPES.LOCUM.name);
			} else {
				return this.getLabel('signUpUserTypeContinueButtonCompany', User.TYPES.PRACTICE.name);
			}
		},

		isLocum() {
			return this.userType === User.TYPES.LOCUM.value;
		},

		isPractice() {
			return this.userType === User.TYPES.PRACTICE.value;
		},

		tenantId() {
			return this.$route.query.tid || process.env.VUE_APP_DEFAULT_TENANT_ID;
		},

		heardFromWhereOptions() {
			const options = this.heardFromOptions;
			if (this.industry) {
				const industryOptions = store.getters['option/industries'].find(({ name }) => name === this.industry)?.heardFromOptions;

				return industryOptions.concat(options);
			}
			return options;
		},

		getHeardFromFields() {
			const additionalFields = this.heardFromWhereOptions.find(({ label }) => label === this.heardFromWhere)?.additionalFields;

			return additionalFields;
		},

		terms() {
			if (this.tenant.settings.links && this.tenant.settings.links.terms) {
				return this.tenant.settings.links.terms;
			}

			return 'https://locumbase.com/terms-and-conditions';
		},
	},

	mounted() {
		const userTypeSelected = this.userType;
		Tracking.track('DisplaySignupForm', { userTypeSelected });

		setTimeout(() => {
			this.setDefualts();
		}, 1000);
		this.loadIndustries(this.tenantId);
		this.LoadHeardFromOptions();
	},
	methods: {
		...mapActions('option', ['LoadIndustries', 'LoadHeardFromOptions']),

		setDefualts() {
			const { industry, email, source, firstname, lastname } = this.$route.query;
			if (industry) {
				const industryName = this.industryList.find(x => x.toLowerCase() === industry.toLowerCase());
				if (industryName) this.industry = industryName;
			}

			if (source) {
				const heardFrom = this.heardFromWhereOptions.find(x => x.text.toLowerCase() === source.toLowerCase());
				if (source) this.heardFromWhere = heardFrom;
			}

			if (email) this.email = email;

			if (firstname) this.firstname = firstname;
			if (lastname) this.lastname = lastname;
		},
		onChangeTelNumber(data) {
			this.phone_number_formatted = data.formattedNumber;
		},

		countryChanged(country) {
			this.country = '+' + country.dialCode;
		},

		check(input) {
			if (input.value != document.getElementById('password').value) {
				input.setCustomValidity('Password Must be Matching.');
			} else {
				input.setCustomValidity(''); // input is valid -- reset the error message
			}
		},

		validate() {
			this.$refs.signupform.validate();
		},

		reset() {
			this.$refs.signupform.reset();
		},

		resetValidation() {
			this.$refs.signupform.resetValidation();
		},

		async loadIndustries() {
			await this.LoadIndustries(this.tenantId);
		},

		async signUp() {
			if (!process.env?.VUE_APP_APPURL) {
				this.generalError = ['Environment not set. Contact technical support.'];
				return;
			}

			if (this.$refs.signupform.validate()) {
				this.loading = true;

				this.generalError = [];
				const { password, phone_number_formatted } = this;
				const industry = this.industry;
				const username = this.email.toLowerCase().trim();
				const firstname = this.firstname;
				const lastname = this.lastname;
				const email = this.email.toLowerCase().trim();

				let signup_response = null;

				try {
					const attributes = {
						email,
						phone_number: phone_number_formatted,
						family_name: lastname,
						name: firstname,
						profile: industry,
						locale: this.isPractice ? 'company' : 'professional',
						'custom:heardFromWhere': this.heardFromWhere,
						'custom:heardFromWhereText': this.heardFromWhereText,
					};

					const tenantId = this.tenantId;

					const resp = await fetchTenant(tenantId);

					if (resp.tenantId) {
						attributes['custom:tenant'] = resp.tenantId;
					} else {
						this.generalError = ['Incorrect Tenant Id'];
						return;
					}

					signup_response = await Auth.signUp({
						username,
						password,
						attributes,
					});
				} catch (err_response) {
					this.generalError.push(err_response.message);
					if (err_response.message == 'Invalid phone number format.') {
						this.generalError.push('Please use format +27 123456789');
					}

					this.errorSnackbar = true;
					this.loading = false;

					return;
				}

				try {
					await axios.post(process.env.VUE_APP_APIURL + '/users/cognito/addtogroup', {
						username: signup_response.userSub,
						groupname: this.isPractice ? 'CompanyAdmin' : 'Locum',
					});

					window.sendinblue.identify(email, {
						SMS: phone_number_formatted,
						LASTNAME: lastname,
						FIRSTNAME: firstname,
						USER_ID: signup_response.userSub,
						ACCOUNT_TYPE: this.isPractice ? 'company' : 'professional',
						INDUSTRY: industry,
						EMAIL_VERIFIED: false,
						HEARD_FROM: this.heardFromWhere,
						TENANT_ID: this.tenantId,
					});

					const type = this.isPractice ? 'company' : 'professional';

					const tenant = this.tenantId;

					Tracking.setUser(email, firstname, lastname, type, industry, tenant);

					store.commit('SET_USERNAME', username);

					this.$router.push('/confirm-signup');
				} catch (err_response) {
					this.generalError.push(err_response.message);
					this.errorSnackbar = true;
				} finally {
					this.loading = false;
				}
			}
		},
	},
};
</script>
